/**
 * The viewer's `Version` control, rendered by the snapshot picker (`ReportVersionSelector`): a deck
 * has hundreds of vintages, so it needs search, tagged snapshots and a date filter rather than a
 * flat dropdown. This hook only assembles the `{control, choices}` pair that component takes.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { SelectProps } from '@amzn/awsui-components-core/select';
import type { PnlDataVersion, VersionChoices } from 'src/components/star-grid/reportSelectors.types';
import { listPnlVersions } from 'src/components/star-grid/services/starGridService';
import { selectedVersionOption, versionOptions } from 'src/components/star-grid/utils/reportSelectorOptions';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useSelectorCatalog } from 'src/components/star-grid/hooks/useReportSelectors';

const LIVE_VERSION = 'Live';

export interface UseReportVersionPickerResult {
  readonly control: SelectProps;
  readonly choices: VersionChoices;
  /**
   * The instant to query at, or `undefined` for Live.
   *
   * Undefined rather than an empty string so the caller can omit `as_of` entirely, letting an untouched
   * report post exactly the body it always did.
   */
  readonly asOf: string | undefined;
}

/**
 * `initial` is the control's saved value (a version name or an instant), shown until the user picks;
 * `onPick` receives each pick as the value a comparison cell reads: the instant, or `Live`.
 */
export const useReportVersionPicker = (reportId?: string, initial?: string, onPick?: (value: string) => void): UseReportVersionPickerResult => {
  const text = useStarGridText();
  const [historyIncomplete, setHistoryIncomplete] = useState(false);
  const [asOf, setAsOf] = useState<string | undefined>(undefined);
  const [picked, setPicked] = useState(false);
  const pickRef = useRef(onPick);
  pickRef.current = onPick;
  const fetchVersions = useCallback(async () => {
    setHistoryIncomplete(false);
    return listPnlVersions(() => setHistoryIncomplete(true));
  }, []);
  const catalog = useSelectorCatalog(
    fetchVersions,
    {
      placeholder: text('chooseVersion'),
      loadingText: text('loadingVersions'),
      errorText: text('versionsUnavailable'),
      empty: text('noVersions')
    },
    reportId ? Number(reportId) : undefined,
    false
  );
  const versions = useMemo(() => catalog.data ?? [], [catalog.data]);
  // Live carries no instant, so picking it clears `as_of` rather than sending a timestamp meaning "now".
  // An unresolvable vintage is refused: serving Live under its label would be a lie about the numbers.
  const select = useCallback((version: PnlDataVersion): void => {
    if (version.resolvable === false) return;
    setAsOf(version.as_of ?? undefined);
    setPicked(true);
    pickRef.current?.(version.as_of ?? LIVE_VERSION);
  }, []);
  const shownAsOf = picked || !initial ? asOf : versions.find((item) => item.version === initial || item.as_of === initial)?.as_of ?? undefined;
  // Until the picker is first opened the catalog is unread, so the chip shows the saved value as stated.
  const unread = catalog.data === undefined;
  const control: SelectProps = {
    ...catalog.control,
    options: versionOptions(versions, text('versionHistoryUnavailable')),
    selectedOption: unread ? { value: initial ?? LIVE_VERSION, label: initial ?? LIVE_VERSION } : selectedVersionOption(versions, shownAsOf),
    onChange: ({ detail }) => {
      const picked = versions.find((item) => item.version === detail.selectedOption.value);
      if (picked) select(picked);
    }
  };
  return { control, choices: { versions, select, retry: catalog.retry, historyIncomplete, load: catalog.load }, asOf };
};
